import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '@/config/env';
import { STORAGE_KEYS } from '@/constants/storage';
import type { ApiResponse } from '@/types/api';
import type { LoginResponse } from '@/types/auth';
import { ApiError } from '@/utils/apiError';
import {
  dispatchModuleAccessDenied,
  isModuleNotEnabledMessage,
  parseModuleFromAccessMessage,
} from '@/utils/moduleNavigation';
import { getModuleLoginPath } from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { clearEntryModuleStorage, readStoredEntryModule, readStoredWorkspaceModule } from '@/utils/moduleScope';
import { purgeLegacyAuthFromBrowser } from '@/core/security/purgeLegacyAuthStorage';

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: () => void;
  reject: (err: unknown) => void;
}> = [];

function resolveLoginUrl(): string {
  const entryModule = readStoredEntryModule() ?? readStoredWorkspaceModule();
  if (entryModule) {
    return getModuleLoginPath(entryModule);
  }
  return MODULE_HUB_PATH;
}

function processQueue(error: unknown) {
  refreshQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve();
  });
  refreshQueue = [];
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const companyRaw = localStorage.getItem(STORAGE_KEYS.SELECTED_COMPANY);
  if (companyRaw && config.headers) {
    try {
      const company = JSON.parse(companyRaw) as { id?: number };
      if (company.id) {
        config.headers['X-Company-Id'] = String(company.id);
      }
    } catch {
      /* ignore */
    }
  }
  const storeRaw = localStorage.getItem(STORAGE_KEYS.SELECTED_STORE);
  if (storeRaw && config.headers) {
    try {
      const store = JSON.parse(storeRaw) as { id?: number };
      if (store.id) {
        config.headers['X-Store-Id'] = String(store.id);
      }
    } catch {
      /* ignore */
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && original && !original._retry) {
      const isAuthEndpoint =
        original.url?.includes('/v1/auth/login') ||
        original.url?.includes('/v1/auth/refresh') ||
        original.url?.includes('/v1/auth/session');

      if (isAuthEndpoint) {
        return Promise.reject(ApiError.fromUnknown(error));
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: () => resolve(api(original)),
            reject,
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        await axios.post<ApiResponse<LoginResponse>>(
          `${getApiBaseUrl()}/v1/auth/refresh`,
          {},
          { withCredentials: true },
        );
        processQueue(null);
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError);
        clearAuthStorage();
        window.location.href = resolveLoginUrl();
        return Promise.reject(ApiError.fromUnknown(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    const apiMessage =
      typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : typeof error.response?.data?.error === 'string'
          ? error.response.data.error
          : undefined;
    if (error.response?.status === 403 && isModuleNotEnabledMessage(apiMessage)) {
      dispatchModuleAccessDenied({
        message: apiMessage ?? 'Module is not enabled for this organization',
        module: apiMessage ? parseModuleFromAccessMessage(apiMessage) : null,
      });
    }

    return Promise.reject(ApiError.fromUnknown(error));
  },
);

export function clearAuthStorage() {
  purgeLegacyAuthFromBrowser();
  clearEntryModuleStorage();
}

/** Cookies carry tokens — no client-side token persistence. */
export function setAuthTokens(_accessToken: string, _refreshToken: string) {
  /* intentionally empty */
}

export async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  try {
    const { data } = await promise;
    if (!data.success) {
      throw ApiError.fromResponse(data);
    }
    return data.data as T;
  } catch (err) {
    throw ApiError.fromUnknown(err);
  }
}

export default api;
